import React, { useState, useEffect, useRef, useCallback } from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { compose } from "utils/WebUtils";
import { makeStyles } from "@material-ui/core/styles";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography
} from "@material-ui/core";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import GetAppIcon from "@material-ui/icons/GetApp";
import StarsIcon from "@material-ui/icons/Stars";
import {
    currentOrganization,
    currentUser,
    getOrgConfigurations
} from "redux/selectors";
import { ackReportsMigrationNotice } from "rest/API";
import {
    MIGRATION_TIMELINE,
    isLegacyReportingMigrationCohort,
    isModalSeenLocally,
    markModalSeenLocally,
    isModalSeenForUser,
    reportsV2Path
} from "./reportsMigrationCohort";

const useStyles = makeStyles((theme) => ({
    paper: {
        width: 480,
        maxWidth: "calc(100% - 32px)"
    },
    title: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: theme.spacing(2.5, 3, 1)
    },
    pill: {
        display: "inline-block",
        backgroundColor: theme.palette.background.primary[100],
        color: theme.palette.background.primary[800],
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        padding: "3px 10px",
        borderRadius: 20,
        marginBottom: theme.spacing(1)
    },
    content: {
        padding: theme.spacing(2, 3)
    },
    valueProp: {
        display: "flex",
        alignItems: "flex-start",
        marginBottom: theme.spacing(1.5)
    },
    vpIcon: {
        width: 32,
        height: 32,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        marginRight: theme.spacing(1.5)
    },
    vpIconFluid: {
        backgroundColor: theme.palette.background.primary[50],
        color: theme.palette.background.primary[600]
    },
    vpIconExport: {
        backgroundColor: theme.palette.background.info[50],
        color: theme.palette.background.info[600]
    },
    vpIconEnhance: {
        backgroundColor: theme.palette.background.green[50],
        color: theme.palette.background.green[600]
    },
    vpDesc: {
        fontWeight: 400
    },
    note: {
        backgroundColor: theme.palette.background.orange[50],
        border: `1px solid ${theme.palette.background.orange[100]}`,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(1.5),
        margin: theme.spacing(0.5, 0, 1.5)
    },
    noteHeading: {
        color: theme.palette.background.orange[800],
        fontWeight: 600,
        fontSize: 12,
        marginBottom: theme.spacing(1)
    },
    noteRow: {
        display: "flex",
        marginBottom: theme.spacing(0.75)
    },
    noteDate: {
        color: theme.palette.background.orange[800],
        fontWeight: 600,
        fontSize: 12,
        minWidth: 44,
        flexShrink: 0
    },
    noteText: {
        color: theme.palette.background.orange[700],
        fontSize: 12,
        lineHeight: 1.45
    },
    tsm: {
        fontWeight: 400,
        color: theme.palette.background.neutral[500]
    },
    tsmStrong: {
        fontWeight: 600,
        color: theme.palette.background.neutral[600]
    },
    actions: {
        flexDirection: "column",
        alignItems: "stretch",
        padding: theme.spacing(0, 3, 2.5),
        "& > :not(:first-child)": {
            marginLeft: 0,
            marginTop: theme.spacing(0.5)
        }
    },
    remindLater: {
        color: theme.palette.background.neutral[500]
    }
}));

export function ReportsMigrationModal(props) {
    const { currentOrganization, currentUser, orgConfigs, history } = props;
    const classes = useStyles();

    const [open, setOpen] = useState(false);
    const decidedForOrgRef = useRef(null);

    const orgId = currentOrganization && currentOrganization.id;
    const userId =
        (currentUser && (currentUser.username || currentUser.id)) || "";
    const inCohort = isLegacyReportingMigrationCohort(
        currentOrganization,
        orgConfigs,
        currentUser
    );

    useEffect(() => {
        // Wait until org, user and config are all loaded so cohort/seen checks
        // are reliable. Decide once per org (re-evaluate if the org changes).
        if (
            !orgId ||
            !currentUser ||
            !Array.isArray(orgConfigs) ||
            orgConfigs.length === 0
        ) {
            return;
        }
        if (decidedForOrgRef.current === orgId) return;
        decidedForOrgRef.current = orgId;

        const seen =
            isModalSeenLocally(orgId, userId) ||
            isModalSeenForUser(currentUser, orgId);

        if (inCohort && !seen) {
            setOpen(true);
        } else {
            setOpen(false);
            if (seen) markModalSeenLocally(orgId, userId);
        }
    }, [orgId, currentUser, orgConfigs, inCohort, userId]);

    const markSeen = useCallback(
        async (action) => {
            setOpen(false);
            if (!orgId) return;
            markModalSeenLocally(orgId, userId);
            try {
                await ackReportsMigrationNotice(orgId, action);
            } catch (err) {
                console.log(
                    `Unable to persist reports migration notice ack: ${
                        (err && (err.message || err.text)) || err
                    }`
                );
            }
        },
        [orgId, userId]
    );

    const handleExplore = useCallback(() => {
        markSeen("explore");
        if (orgId) history.push(reportsV2Path(orgId));
    }, [markSeen, orgId, history]);

    const handleRemindLater = useCallback(() => {
        markSeen("remind_later");
    }, [markSeen]);

    if (!open) return null;

    const valueProps = [
        {
            Icon: FlashOnIcon,
            iconClass: classes.vpIconFluid,
            title: "A more fluid experience",
            desc: "Faster load times and a smoother, modern interface."
        },
        {
            Icon: GetAppIcon,
            iconClass: classes.vpIconExport,
            title: "Larger data exports",
            desc: "Export 1M+ rows — up from the previous limit."
        },
        {
            Icon: StarsIcon,
            iconClass: classes.vpIconEnhance,
            title: "Other major enhancements",
            desc: "No Power User license needed, plus more."
        }
    ];

    return (
        <Dialog
            open={open}
            maxWidth={false}
            classes={{ paper: classes.paper }}
            disableBackdropClick
            disableEscapeKeyDown
            aria-labelledby="reports-v2-migration-title">
            <DialogTitle disableTypography className={classes.title}>
                <span className={classes.pill}>New reporting experience</span>
                <Typography
                    variant="h4"
                    color="primary"
                    id="reports-v2-migration-title">
                    Reports V2 is here — a faster way to run your reports
                </Typography>
            </DialogTitle>
            <DialogContent dividers className={classes.content}>
                {valueProps.map(({ Icon, iconClass, title, desc }) => (
                    <div className={classes.valueProp} key={title}>
                        <div className={`${classes.vpIcon} ${iconClass}`}>
                            <Icon style={{ fontSize: 18 }} />
                        </div>
                        <div>
                            <Typography variant="body1">{title}</Typography>
                            <Typography
                                variant="body2"
                                className={classes.vpDesc}>
                                {desc}
                            </Typography>
                        </div>
                    </div>
                ))}

                <div className={classes.note}>
                    <Typography className={classes.noteHeading}>
                        Legacy reporting is being retired
                    </Typography>
                    <div
                        className={classes.noteRow}
                        style={{ marginBottom: 0 }}>
                        <span className={classes.noteDate}>
                            {MIGRATION_TIMELINE.legacyRemovalDate}
                        </span>
                        <span className={classes.noteText}>
                            All legacy reporting access will be removed from
                            your account.
                        </span>
                    </div>
                </div>

                <Typography variant="body2" className={classes.tsm}>
                    Your Technical Success Manager will reach out and let you
                    know when migration of your{" "}
                    <span className={classes.tsmStrong}>My Reports</span> and{" "}
                    <span className={classes.tsmStrong}>Shared Reports</span>{" "}
                    begins. No action needed yet.
                </Typography>
            </DialogContent>
            <DialogActions className={classes.actions}>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    disableElevation
                    onClick={handleExplore}>
                    Explore Reports V2
                </Button>
                <Button
                    fullWidth
                    className={classes.remindLater}
                    onClick={handleRemindLater}>
                    Remind me later
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function mapStateToProps(state) {
    return {
        currentOrganization: currentOrganization(state),
        currentUser: currentUser(state),
        orgConfigs: getOrgConfigurations(state)
    };
}

export default compose(
    withRouter,
    connect(mapStateToProps)
)(ReportsMigrationModal);
